import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Clock, Layers, Briefcase, Zap, AlertTriangle, TrendingUp, ShieldAlert,
  ShieldCheck, Lightbulb, Target, Activity, ArrowRight, ArrowLeftRight,
  CheckCircle2, AlertCircle, ArrowUpRight, AppWindow, Gauge,
} from "lucide-react";
import { useDashTheme } from "../hooks/useDashTheme";

const EASE = [0.16, 1, 0.3, 1];

const SEVERITY = {
  high:   { color: "#E5484D", bg: "rgba(229,72,77,0.08)",  ring: "rgba(229,72,77,0.24)",  label: "High" },
  medium: { color: "#D97706", bg: "rgba(217,119,6,0.08)",  ring: "rgba(217,119,6,0.24)",  label: "Medium" },
  low:    { color: "#0EA5E9", bg: "rgba(14,165,233,0.08)", ring: "rgba(14,165,233,0.22)", label: "Low" },
};
const PRIORITY = { high: "#E5484D", medium: "#D97706", low: "#16A34A" };
const RISK = {
  low:    { color: "#16A34A", bg: "rgba(22,163,74,0.12)",  icon: ShieldCheck, label: "Low risk" },
  medium: { color: "#D97706", bg: "rgba(217,119,6,0.12)",  icon: ShieldAlert, label: "Medium risk" },
  high:   { color: "#E5484D", bg: "rgba(229,72,77,0.12)",  icon: ShieldAlert, label: "High risk" },
};

const ACCENT = ["#5B5BD6", "#0EA5E9", "#10B981", "#F59E0B", "#EC4899", "#8B5CF6"];

// ── Org-intel metrics strip (from `metrics` event) ──────────────────────────
export function OrgIntelMetricsStrip({ metrics, streaming }) {
  const t = useDashTheme();
  if (!metrics) return null;
  const {
    totalHours = 0, sessionCount, projectCount = 0, daysInRange = 1,
    avgHoursPerDay = 0, dailyTrend = [], appBreakdown = [], copyFlows = [],
    source = "app",
  } = metrics;
  const isStealth = source === "stealth";
  const H = Math.floor(totalHours), M = Math.round((totalHours - H) * 60);
  const hoursLabel = H > 0 && M > 0 ? `${H}h ${M}m` : H > 0 ? `${H}h` : `${M}m`;
  const avgH = Math.floor(avgHoursPerDay), avgM = Math.round((avgHoursPerDay - avgH) * 60);
  const avgLabel = avgH > 0 && avgM > 0 ? `${avgH}h ${avgM}m` : avgH > 0 ? `${avgH}h` : `${avgM}m`;
  const maxDay = Math.max(...dailyTrend.map((d) => d.hours), 0.01);
  const showTrend = dailyTrend.length > 1;
  const showDonut = appBreakdown.length > 0;

  // % of a nominal 8h workday (for the hours ring)
  const hoursPct = Math.min(100, Math.round((totalHours / 8) * 100));
  const avgPct = Math.min(100, Math.round((avgHoursPerDay / 8) * 100));
  const trendVals = dailyTrend.map((d) => d.hours);

  // KPI tiles adapt to source. Each carries a `visual` (mini data-viz on the right).
  const kpis = isStealth
    ? [
        { icon: <Clock size={16} />, label: "Total hours", value: hoursLabel, color: "#5B5BD6", bg: "rgba(91,91,214,0.11)", pulse: true, visual: <Ring pct={hoursPct} color="#5B5BD6" t={t} /> },
        { icon: <Gauge size={16} />, label: "Avg / day", value: avgLabel, color: "#0EA5E9", bg: "rgba(14,165,233,0.11)", visual: <Ring pct={avgPct} color="#0EA5E9" t={t} /> },
        { icon: <AppWindow size={16} />, label: "Apps used", value: appBreakdown.length || "—", color: "#10B981", bg: "rgba(16,185,129,0.11)", visual: <MiniBars data={appBreakdown.map((a) => a.hours)} color="#10B981" t={t} /> },
        { icon: <Activity size={16} />, label: "Days", value: daysInRange, color: "#F59E0B", bg: "rgba(245,158,11,0.11)", visual: trendVals.length > 1 ? <Spark data={trendVals} color="#F59E0B" t={t} /> : <Dots n={daysInRange} color="#F59E0B" t={t} /> },
      ]
    : [
        { icon: <Clock size={16} />, label: "Total hours", value: hoursLabel, color: "#5B5BD6", bg: "rgba(91,91,214,0.11)", pulse: true, visual: <Ring pct={hoursPct} color="#5B5BD6" t={t} /> },
        { icon: <Layers size={16} />, label: "Sessions", value: sessionCount ?? 0, color: "#0EA5E9", bg: "rgba(14,165,233,0.11)", visual: <Dots n={sessionCount ?? 0} color="#0EA5E9" t={t} /> },
        { icon: <Briefcase size={16} />, label: "Projects", value: projectCount || "—", color: "#10B981", bg: "rgba(16,185,129,0.11)", visual: <Dots n={projectCount} color="#10B981" t={t} /> },
        { icon: <Activity size={16} />, label: "Days", value: daysInRange, color: "#F59E0B", bg: "rgba(245,158,11,0.11)", visual: trendVals.length > 1 ? <Spark data={trendVals} color="#F59E0B" t={t} /> : <Dots n={daysInRange} color="#F59E0B" t={t} /> },
      ];

  return (
    <motion.div layout initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }}
      style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
      {/* KPI row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
        {kpis.map((k, i) => (
          <Kpi key={i} t={t} icon={k.icon} label={k.label} value={k.value} color={k.color} bg={k.bg} streaming={k.pulse && streaming} visual={k.visual} />
        ))}
      </div>

      {/* Chart row: hours-per-day trend + app-mix donut side by side */}
      {(showTrend || showDonut) && (
        <div style={{ display: "grid", gridTemplateColumns: showTrend && showDonut ? "3fr 2fr" : "1fr", gap: 10 }}>
          {showTrend && (
            <ChartCard t={t} icon={<TrendingUp size={12} />} title="Hours per day">
              <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 84 }}>
                {dailyTrend.map((d, i) => {
                  const h = Math.max(5, (d.hours / maxDay) * 74);
                  const hot = d.hours / maxDay > 0.8;
                  return (
                    <div key={i} title={`${d.date}: ${d.hours}h`} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4, minWidth: 0 }}>
                      <motion.div initial={{ height: 0 }} animate={{ height: h }} transition={{ delay: 0.1 + i * 0.02, duration: 0.5, ease: EASE }}
                        style={{ width: "72%", maxWidth: 24, borderRadius: "4px 4px 2px 2px", background: hot ? "linear-gradient(180deg, #818CF8, #5B5BD6)" : t.isDark ? "rgba(139,139,245,0.45)" : "rgba(91,91,214,0.32)", boxShadow: hot ? "0 3px 10px rgba(91,91,214,0.35)" : "none" }} />
                      <span style={{ fontSize: 8.5, color: t.c.textTertiary }}>{d.date.slice(8)}</span>
                    </div>
                  );
                })}
              </div>
            </ChartCard>
          )}
          {showDonut && (
            <ChartCard t={t} icon={<AppWindow size={12} />} title={isStealth ? "App mix" : "Focus split"}>
              <AppMixDonut data={appBreakdown} t={t} />
            </ChartCard>
          )}
        </div>
      )}

      {copyFlows.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, color: t.c.textTertiary, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            <ArrowLeftRight size={12} /> Data movement
          </span>
          {copyFlows.map((c, i) => {
            const [src, dst] = c.flow.split(" → ");
            return (
              <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 99, background: t.c.surfaceMuted, border: `1px solid ${t.c.border}`, fontSize: 11.5, fontWeight: 600, color: t.c.text }}>
                {src} <ArrowRight size={11} style={{ color: "#5B5BD6" }} /> {dst}{c.count > 1 ? ` ·${c.count}` : ""}
              </span>
            );
          })}
        </div>
      )}
    </motion.div>
  );
}

function ChartCard({ t, icon, title, children }) {
  return (
    <div style={{ borderRadius: 14, background: t.c.surface, border: `1px solid ${t.c.border}`, boxShadow: `0 2px 8px ${t.c.shadowColor}`, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "11px 14px 9px", borderBottom: `1px solid ${t.c.border}` }}>
        <span style={{ color: t.c.textTertiary, display: "inline-flex" }}>{icon}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: t.c.textTertiary, textTransform: "uppercase", letterSpacing: "0.06em" }}>{title}</span>
      </div>
      <div style={{ padding: "12px 14px" }}>{children}</div>
    </div>
  );
}

function AppMixDonut({ data, t }) {
  const top = data.slice(0, 6);
  const total = top.reduce((s, d) => s + d.hours, 0) || 1;
  const size = 78, thickness = 12, r = (size - thickness) / 2, circ = 2 * Math.PI * r;
  let acc = 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
      <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
        <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={t.isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"} strokeWidth={thickness} />
          {top.map((d, i) => {
            const dash = (d.hours / total) * circ;
            const seg = (
              <motion.circle key={i} cx={size / 2} cy={size / 2} r={r} fill="none" stroke={ACCENT[i % ACCENT.length]} strokeWidth={thickness} strokeLinecap="butt"
                strokeDasharray={`${Math.max(0, dash - 1.5)} ${circ - Math.max(0, dash - 1.5)}`}
                initial={{ strokeDashoffset: circ }} animate={{ strokeDashoffset: -acc }} transition={{ duration: 0.8, delay: 0.15 + i * 0.1, ease: EASE }} />
            );
            acc += dash; return seg;
          })}
        </svg>
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <span style={{ fontSize: 16, fontWeight: 800, color: t.c.text, lineHeight: 1 }}>{top.length}</span>
          <span style={{ fontSize: 8, color: t.c.textTertiary, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>apps</span>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 5, flex: 1, minWidth: 120 }}>
        {top.slice(0, 5).map((d, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: ACCENT[i % ACCENT.length], flexShrink: 0 }} />
            <span style={{ flex: 1, fontSize: 11.5, color: t.c.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.name}</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: t.c.text }}>{Math.round((d.hours / total) * 100)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Kpi({ t, icon, label, value, color, bg, streaming, visual }) {
  return (
    <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.35, ease: EASE }}
      style={{ borderRadius: 14, padding: "14px 16px", background: t.c.surface, border: `1px solid ${t.c.border}`, boxShadow: `0 2px 8px ${t.c.shadowColor}`, display: "flex", alignItems: "center", gap: 12, justifyContent: "space-between" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
        <span style={{ display: "inline-flex", width: 34, height: 34, borderRadius: 10, alignItems: "center", justifyContent: "center", background: bg, color, flexShrink: 0 }}>{icon}</span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: t.c.textTertiary, textTransform: "uppercase", letterSpacing: "0.07em" }}>{label}</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: t.c.text, letterSpacing: "-0.03em", lineHeight: 1.1, fontVariantNumeric: "tabular-nums", display: "flex", alignItems: "center", gap: 5 }}>
            {value}
            {streaming && label === "Total hours" && <motion.span animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.1, repeat: Infinity }} style={{ width: 5, height: 5, borderRadius: "50%", background: color }} />}
          </div>
        </div>
      </div>
      {visual && <div style={{ flexShrink: 0 }}>{visual}</div>}
    </motion.div>
  );
}

// ── Mini KPI visuals ─────────────────────────────────────────────────────────
function Ring({ pct, color, t }) {
  const size = 40, thickness = 5, r = (size - thickness) / 2, circ = 2 * Math.PI * r;
  const dash = (Math.max(0, Math.min(100, pct)) / 100) * circ;
  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={t.isDark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.07)"} strokeWidth={thickness} />
        <motion.circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={thickness} strokeLinecap="round"
          strokeDasharray={`${dash} ${circ - dash}`} initial={{ strokeDashoffset: circ }} animate={{ strokeDashoffset: 0 }} transition={{ delay: 0.3, duration: 0.9, ease: EASE }} />
      </svg>
      <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9.5, fontWeight: 800, color, fontVariantNumeric: "tabular-nums" }}>{pct}%</span>
    </div>
  );
}

function MiniBars({ data = [], color, t }) {
  const top = data.slice(0, 5);
  const max = Math.max(...top, 0.01);
  if (!top.length) return null;
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 34 }}>
      {top.map((v, i) => (
        <motion.span key={i} initial={{ height: 0 }} animate={{ height: Math.max(5, (v / max) * 34) }} transition={{ delay: 0.2 + i * 0.05, duration: 0.5, ease: EASE }}
          style={{ width: 6, borderRadius: 2, background: color, opacity: 1 - i * 0.13 }} />
      ))}
    </div>
  );
}

function Spark({ data = [], color, t }) {
  const id = React.useId();
  if (data.length < 2) return null;
  const w = 56, h = 34, min = 0, max = Math.max(...data) || 1;
  const step = w / (data.length - 1);
  const pts = data.map((v, i) => [i * step, h - ((v - min) / (max - min || 1)) * (h - 5) - 3]);
  const line = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const area = `${line} L${w},${h} L0,${h} Z`;
  return (
    <svg width={w} height={h} style={{ display: "block" }}>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${id})`} />
      <motion.path d={line} fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"
        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 0.25, duration: 0.9, ease: EASE }} />
    </svg>
  );
}

function Dots({ n = 0, color, t }) {
  const dots = Math.min(n, 9);
  if (!dots) return null;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 4, width: 34 }}>
      {Array.from({ length: dots }).map((_, i) => (
        <motion.span key={i} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.15 + i * 0.04, type: "spring", stiffness: 400, damping: 20 }}
          style={{ width: 8, height: 8, borderRadius: "50%", background: color, opacity: 0.85 }} />
      ))}
      {n > 9 && <span style={{ fontSize: 9, fontWeight: 700, color, gridColumn: "span 3" }}>+{n - 9}</span>}
    </div>
  );
}

// ── Org-intel structured report ─────────────────────────────────────────────
export function OrgIntelligenceRenderer({ report, streaming, partial = false }) {
  const t = useDashTheme();
  if (!report) return null;
  const { headline, summary, riskLevel = "low", issues = [], recommendations = [], patterns = [], rawText } = report;
  const writing = partial || streaming;
  const risk = RISK[riskLevel] || RISK.low;
  const RiskIcon = risk.icon;

  return (
    <motion.div layout initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: EASE }}
      style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Executive hero */}
      <AnimatePresence>
        {headline && (
          <motion.div key="hero" layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4, ease: EASE }}
            style={{ position: "relative", overflow: "hidden", borderRadius: 18, padding: "20px 24px", background: "linear-gradient(120deg, #4F46E5 0%, #5B5BD6 50%, #7C3AED 100%)", boxShadow: "0 12px 36px rgba(91,91,214,0.38)", color: "#fff" }}>
            <div style={{ position: "absolute", top: -30, right: -20, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.10)", filter: "blur(26px)" }} />
            <div style={{ position: "relative" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7, opacity: 0.85 }}>
                  <Zap size={12} /><span style={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.09em" }}>Org Intelligence</span>
                  {writing && <motion.span animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 1, repeat: Infinity }} style={{ fontSize: 10.5, opacity: 0.8 }}>· analyzing</motion.span>}
                </div>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, padding: "4px 11px", borderRadius: 99, background: "rgba(255,255,255,0.20)" }}>
                  <RiskIcon size={13} /> {risk.label}
                </span>
              </div>
              {headline && <p style={{ margin: 0, fontSize: 17, fontWeight: 700, lineHeight: 1.4, letterSpacing: "-0.01em" }}>{headline}</p>}
              {summary && <p style={{ margin: "10px 0 0", fontSize: 13, opacity: 0.9, lineHeight: 1.6 }}>{summary}</p>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Issues detected */}
      <AnimatePresence>
        {issues.length > 0 && (
          <Panel key="issues" t={t} accent="#E5484D" icon={<AlertTriangle size={14} />} title="Issues detected"
            subtitle={`${issues.length} ${issues.length === 1 ? "issue" : "issues"}${writing ? " · analyzing…" : ""}`}>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <AnimatePresence initial={false}>
                {[...issues].sort((a, b) => sevRank(b.severity) - sevRank(a.severity)).map((iss, i) => <IssueCard key={iss.title || i} iss={iss} t={t} />)}
              </AnimatePresence>
            </div>
          </Panel>
        )}
      </AnimatePresence>

      {/* Recommendations */}
      <AnimatePresence>
        {recommendations.length > 0 && (
          <Panel key="recs" t={t} accent="#16A34A" icon={<Lightbulb size={14} />} title="Recommendations"
            subtitle="Actionable improvements">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 10 }}>
              <AnimatePresence initial={false}>
                {recommendations.map((r, i) => <RecCard key={r.title || i} r={r} t={t} />)}
              </AnimatePresence>
            </div>
          </Panel>
        )}
      </AnimatePresence>

      {/* Patterns */}
      <AnimatePresence>
        {patterns.length > 0 && (
          <Panel key="patterns" t={t} accent="#0EA5E9" icon={<Activity size={14} />} title="Patterns observed">
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <AnimatePresence initial={false}>
                {patterns.map((p, i) => {
                  const positive = p.type === "positive";
                  const color = positive ? "#16A34A" : "#D97706";
                  const Icon = positive ? CheckCircle2 : AlertCircle;
                  return (
                    <motion.div key={p.text || i} layout initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.32, ease: EASE }}
                      style={{ display: "flex", gap: 11, padding: "11px 14px", borderRadius: 11, background: `${color}12`, border: `1px solid ${color}26` }}>
                      <span style={{ color, flexShrink: 0, marginTop: 1 }}><Icon size={14} /></span>
                      <span style={{ fontSize: 12.5, color: t.c.textSecondary, lineHeight: 1.55 }}>{p.text}</span>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          </Panel>
        )}
      </AnimatePresence>

      {/* No issues state */}
      {!writing && issues.length === 0 && recommendations.length === 0 && !rawText && (
        <Panel t={t} accent="#16A34A" icon={<ShieldCheck size={14} />} title="Clean period">
          <p style={{ margin: 0, fontSize: 13, color: t.c.textSecondary, lineHeight: 1.6 }}>{summary || "No significant issues or blockers were detected for this period."}</p>
        </Panel>
      )}

      {rawText && issues.length === 0 && (
        <Panel t={t} accent="#5B5BD6" icon={<Activity size={14} />} title="Summary">
          <p style={{ margin: 0, fontSize: 13, color: t.c.textSecondary, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{rawText}</p>
        </Panel>
      )}
    </motion.div>
  );
}

const sevRank = (s) => (s === "high" ? 3 : s === "medium" ? 2 : 1);

function IssueCard({ iss, t }) {
  const meta = SEVERITY[iss.severity] || SEVERITY.low;
  return (
    <motion.div layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.35, ease: EASE }}
      style={{ position: "relative", borderRadius: 12, background: meta.bg, border: `1px solid ${meta.ring}`, padding: "14px 16px", paddingLeft: 18, overflow: "hidden" }}>
      <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: meta.color }} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 9.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em", color: meta.color, background: `${meta.color}1E`, padding: "2px 7px", borderRadius: 6 }}>{meta.label}</span>
          {iss.category && <span style={{ fontSize: 10.5, fontWeight: 600, color: t.c.textTertiary }}>{iss.category}</span>}
        </div>
      </div>
      <div style={{ fontSize: 13.5, fontWeight: 700, color: t.c.text, marginBottom: 4, letterSpacing: "-0.01em" }}>{iss.title}</div>
      {iss.detail && <p style={{ margin: "0 0 6px", fontSize: 12.5, color: t.c.textSecondary, lineHeight: 1.55 }}>{iss.detail}</p>}
      {iss.evidence && (
        <div style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color: meta.color, fontWeight: 600, background: `${meta.color}12`, padding: "3px 8px", borderRadius: 7 }}>
          <Target size={11} /> {iss.evidence}
        </div>
      )}
    </motion.div>
  );
}

function RecCard({ r, t }) {
  const pc = PRIORITY[r.priority] || "#16A34A";
  return (
    <motion.div layout initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35, ease: EASE }}
      style={{ borderRadius: 12, background: t.c.surface, border: `1px solid ${t.c.border}`, boxShadow: `0 1px 6px ${t.c.shadowColor}`, padding: "14px 16px" }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
        <span style={{ display: "inline-flex", width: 26, height: 26, borderRadius: 8, alignItems: "center", justifyContent: "center", background: "rgba(22,163,74,0.14)", color: "#16A34A", flexShrink: 0, marginTop: 1 }}><Lightbulb size={14} /></span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: t.c.text, letterSpacing: "-0.01em" }}>{r.title}</div>
          {r.priority && <span style={{ fontSize: 10, fontWeight: 700, color: pc, textTransform: "uppercase", letterSpacing: "0.05em" }}>{r.priority} priority</span>}
        </div>
      </div>
      {r.action && (
        <div style={{ display: "flex", gap: 7, marginBottom: 7 }}>
          <ArrowRight size={13} style={{ color: "#16A34A", flexShrink: 0, marginTop: 2 }} />
          <span style={{ fontSize: 12.5, color: t.c.textSecondary, lineHeight: 1.5 }}>{r.action}</span>
        </div>
      )}
      {r.impact && (
        <div style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 600, color: "#16A34A", background: "rgba(22,163,74,0.10)", padding: "4px 9px", borderRadius: 8 }}>
          <ArrowUpRight size={12} /> {r.impact}
        </div>
      )}
    </motion.div>
  );
}

function Panel({ t, icon, title, subtitle, accent, children }) {
  return (
    <motion.div layout initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4, ease: EASE }}
      style={{ borderRadius: 16, background: t.c.surface, border: `1px solid ${t.c.border}`, boxShadow: `0 2px 10px ${t.c.shadowColor}`, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 18px", borderBottom: `1px solid ${t.c.border}` }}>
        <span style={{ display: "inline-flex", width: 28, height: 28, borderRadius: 9, alignItems: "center", justifyContent: "center", background: `${accent}16`, color: accent, flexShrink: 0 }}>{icon}</span>
        <div>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: t.c.text, letterSpacing: "-0.01em" }}>{title}</div>
          {subtitle && <div style={{ fontSize: 11.5, color: t.c.textTertiary, marginTop: 1 }}>{subtitle}</div>}
        </div>
      </div>
      <div style={{ padding: "16px 18px" }}>{children}</div>
    </motion.div>
  );
}

export default OrgIntelligenceRenderer;
